import type { PackSelectionValue } from 'app/utils/packSelection';

import type { Refund } from './adminBookings/bookingRefund';
import type { BookingStatus } from './adminBookings/bookings';
import type { User } from './user';

export enum GiftCardStatus {
  Active = 'active',
  Empty = 'empty',
  Expired = 'expired',
  Rejected = 'rejected',
  Pending = 'pending',
  Cancelled = 'cancelled',
}

export enum GiftCardType {
  Email = 'email',
  Print = 'print',
  Physical = 'physical',
  Voucher = 'voucher',
  Incentive = 'incentive',
  Internal = 'internal',
  Manual = 'manual',
}

export type VoucherGiftCardProperties = {
  giftCardType: GiftCardType.Voucher;
  valueType: 'FIX';
  initialValue: number;
  remainingValue: number;
};

type IncentiveBase = {
  giftCardType: GiftCardType.Incentive;
  restrictedPackageIds?: number[];
  restrictedPackIds?: number[];
};
type IncentiveWithPercent = IncentiveBase & {
  valueType: 'PERCENT';
  discount: number;
};
type IncentiveWithFix = IncentiveBase & {
  valueType: 'FIX';
  initialValue: number;
  remainingValue: number;
};

export type IncentiveGiftCardProperties =
  IncentiveWithPercent | IncentiveWithFix;

export type InternalGiftCardProperties = {
  giftCardType: GiftCardType.Internal;
  valueType: 'FIX';
  initialValue: number;
  remainingValue: number;
};

export type ManualGiftCardProperties = {
  giftCardType: GiftCardType.Manual;
  valueType: 'FIX';
  initialValue: number;
  remainingValue: number;
};

export type EmailGiftCardProperties = {
  giftCardType: GiftCardType.Email;
  color: string;
  message: string;
  beneficiaryEmail: string;
  billingEmail: string;
  emailSendDate: string;
  valueType: 'FIX';
  initialValue: number;
  remainingValue: number;
};

export type PrintGiftCardProperties = {
  giftCardType: GiftCardType.Print;
  color: string;
  message: string;
  valueType: 'FIX';
  initialValue: number;
  remainingValue: number;
};

export type PhysicalGiftCardProperties = {
  giftCardType: GiftCardType.Physical;
  shippingFirstName: string;
  shippingLastName: string;
  shippingStreet1: string;
  shippingStreet2: string;
  shippingZipCode: string;
  shippingCity: string;
  shippingCountryId: number;
  valueType: 'FIX';
  initialValue: number;
  remainingValue: number;
};

export type GiftCard = {
  id: number;
  code: string;
  createdAt: string;
  countryId: number;
  startDate?: string;
  expirationDate?: string;
  status: GiftCardStatus;
  maxUse?: number;
  minAmount?: number;
  onlyFirstBooking?: boolean;
  senderName: string;
  beneficiaryName: string;
  totalAmount?: number;
  cardInfo?: {
    last4: string;
  };
  billingFirstName?: string;
  billingEmail?: string;
  currency: string;
} & (
  | EmailGiftCardProperties
  | PrintGiftCardProperties
  | PhysicalGiftCardProperties
  | VoucherGiftCardProperties
  | IncentiveGiftCardProperties
  | InternalGiftCardProperties
  | ManualGiftCardProperties
);

export type GiftCardListParams = {
  offset: number;
  search?: string;
  sorting?: string;
  status: GiftCardStatus[];
  mytype: GiftCardType[];
};

export type GiftCardBooking = {
  code: string;
  date: string;
  couponAmount: number;
  status: BookingStatus;
};

export type GiftCardDetails = {
  giftCard: GiftCard;
  giftCardBookings: GiftCardBooking[];
  staycations: number[];
  giftCards: number[];
  user: User;
  refunds: Refund[];
};

export type RefundForm = {
  amountCb?: number;
  reason?: number;
  comment?: string;
  deactivateGiftCard: boolean;
};

export type GiftCardEditForm = {
  startDate?: string;
  expirationDate: string;
  remainingValue: number;
  discount?: number;
  minAmount?: number;
  maxUse?: number;
  restrictedPacks?: PackSelectionValue[];
  restrictedPackageIds?: number[];
  restrictedPackIds?: number[];
  senderName: string;
  beneficiaryName: string;
  color?: string;
  beneficiaryEmail?: string;
  billingEmail?: string;
  emailSendDate?: string;
  message?: string;
  shippingFirstName?: string;
  shippingLastName?: string;
  shippingStreet1?: string;
  shippingStreet2?: string;
  shippingZipCode?: string;
  shippingCity?: string;
  shippingCountryId?: number;
};
